import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { getMe, logoutRequest, readStoredCreds, STORAGE_KEY, type Credentials } from "@/api";

// Two auth lanes, one authenticated identity (ADR-0001):
//   - "basic":  operator/tenant_admin password kept in sessionStorage (creds set).
//   - "cookie": Google session — HttpOnly cookie, invisible to JS (creds === null),
//               discovered by probing /me. STILL authenticated.
//   - "out":    no creds and the /me probe failed.
// `status` is the auth *intent*; `creds` is kept only for the Authorization
// header (read inside the api client) and the username display. Gating data on
// `creds` truthiness would break the cookie lane — code keys off `status` /
// `isAuthenticated` instead.
export type AuthStatus = "checking" | "basic" | "cookie" | "out";

interface AuthState {
  creds: Credentials | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  login: (creds: Credentials) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [creds, setCreds] = useState<Credentials | null>(readStoredCreds);
  // Basic creds present => authenticated immediately, no probe needed.
  const [status, setStatus] = useState<AuthStatus>(creds ? "basic" : "checking");

  // Resolve the cookie lane: with no Basic creds, probe /me once. Success means
  // a live Google session ("cookie"); failure means "out". This is the one
  // place the probe lives — RequireAuth reads the result.
  useEffect(() => {
    if (creds) {
      setStatus("basic");
      return;
    }
    let active = true; // guard against StrictMode double-invoke / unmount
    setStatus("checking");
    getMe()
      .then(() => active && setStatus("cookie"))
      .catch(() => active && setStatus("out"));
    return () => {
      active = false;
    };
  }, [creds]);

  const login = (c: Credentials) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    setCreds(c);
  };
  const logout = async () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setCreds(null);
    try {
      await logoutRequest(); // drop the cookie if a Google session is active
    } catch {
      /* best-effort */
    }
  };

  const isAuthenticated = status === "basic" || status === "cookie";
  return (
    <AuthContext.Provider value={{ creds, status, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "checking") return null;
  if (status === "out") return <Navigate to="/login" replace />;
  return <>{children}</>;
}
